// 常见图表的option设置
// echarts/module/chart-presets.ts

import type { EChartsOption } from 'echarts'
import { darkTheme, lightTheme } from '@/utils/echarts-theme'
import { CallbackDataParams, TopLevelFormatterParams } from 'echarts/types/dist/shared'
import { DualBarItem, QuadrantBubbleItem, QuadrantConfig, QuadrantMap, SingleBarItem } from '../type/index.module'

type FormatterFn = (val: number | string) => string
function defaultValueFormatter(unit?: string): FormatterFn {
  return (v) => {
    if (v === null || v === undefined) return '-'
    const n = typeof v === 'number' ? Number(v).toLocaleString() : String(v)
    return unit ? `${n}${unit}` : n
  }
}
function ensureColors(custom?: string[]) {
  if (custom && custom.length) return custom
  return (darkTheme.color as string[]) ?? ['#00e0ff', '#2fb9ff', '#6ad5ff', '#7bffb8', '#ffd36a']
}

/** rankBarOption */
export function rankBarOption(
  data: { labels: string[]; values: number[] },
  opts?: {
    title?: string
    colorPalette?: string[]
    labelFormatter?: (v: number) => string
    unit?: string
    valueFormatter?: FormatterFn
  }
): EChartsOption {
  const colors = ensureColors(opts?.colorPalette)
  const valueFmt = opts?.valueFormatter ?? defaultValueFormatter(opts?.unit)
  return {
    backgroundColor: darkTheme.backgroundColor,
    title: { text: opts?.title ?? '', left: 'left', textStyle: { color: darkTheme.title?.textStyle?.color } },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const p = params[0]
        return `${p.name}<br/>${p.seriesName}: ${valueFmt(p.value)}`
      }
    },
    grid: { left: '6%', right: '6%', top: 50, bottom: 20 },
    xAxis: { type: 'value', show: false },
    yAxis: {
      type: 'category',
      data: data.labels,
      inverse: true,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: { color: darkTheme.textStyle?.color }
    },
    series: [
      {
        name: opts?.title ?? '数值',
        type: 'bar',
        data: data.values,
        barWidth: 14,
        itemStyle: { color: (params: any) => colors[params.dataIndex % colors.length] },
        label: {
          show: true,
          position: 'right',
          color: darkTheme.textStyle?.color,
          formatter: ({ value }: any) => valueFmt(value)
        }
      }
    ]
  }
}

/** donutOption */
export function donutOption(
  data: Array<{ name: string; value: number }>,
  opts?: {
    title?: string
    unit?: string
    colorPalette?: string[]
    labelFormatter?: (name: string, value: number) => string
  }
): EChartsOption {
  const colors = ensureColors(opts?.colorPalette)
  const total = data.reduce((s, d) => s + (Number(d.value) || 0), 0)
  const unit = opts?.unit ?? ''
  return {
    backgroundColor: darkTheme.backgroundColor,
    title: { text: opts?.title ?? '', left: 'center', top: 8, textStyle: { color: darkTheme.title?.textStyle?.color } },
    tooltip: { trigger: 'item', formatter: (p: any) => `${p.name}<br/>${p.value}${unit} (${p.percent}%)` },
    legend: { orient: 'vertical', left: 'left', textStyle: { color: darkTheme.textStyle?.color } },
    series: [
      {
        name: opts?.title ?? '占比',
        type: 'pie',
        radius: ['50%', '72%'],
        avoidLabelOverlap: false,
        label: {
          show: true,
          position: 'outside',
          color: darkTheme.textStyle?.color,
          formatter: (params: any) => {
            if (opts?.labelFormatter) return opts.labelFormatter(params.name, params.value)
            return `${params.name}\n${params.percent}%`
          }
        },
        data,
        color: colors
      }
    ],
    graphic: [
      {
        type: 'text',
        left: 'center',
        top: '45%',
        style: {
          text: `${total}${unit}`,
          textAlign: 'center',
          fill: darkTheme.textStyle?.color ?? '#e6f7ff',
          fontSize: 18
        }
      }
    ]
  }
}

/** multiLineOption */
export function multiLineOption(
  data: { x: string[]; series: Array<{ name: string; data: number[] }> },
  opts?: { title?: string; colorPalette?: string[]; unit?: string; valueFormatter?: FormatterFn }
): EChartsOption {
  const colors = ensureColors(opts?.colorPalette)
  const valueFmt = opts?.valueFormatter ?? defaultValueFormatter(opts?.unit)
  return {
    backgroundColor: darkTheme.backgroundColor,
    title: { text: opts?.title ?? '', left: 'left', textStyle: { color: darkTheme.title?.textStyle?.color } },
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const rows = params.map((p: any) => `${p.seriesName}: ${valueFmt(p.value)}`)
        return `${params[0].axisValue}<br/>${rows.join('<br/>')}`
      }
    },
    legend: { top: 8, textStyle: { color: darkTheme.textStyle?.color } },
    grid: { left: '6%', right: '6%', top: 48, bottom: 30 },
    xAxis: {
      type: 'category',
      data: data.x,
      axisLine: { lineStyle: { color: 'rgba(255,255,255,0.06)' } },
      axisLabel: { color: darkTheme.textStyle?.color }
    },
    yAxis: {
      type: 'value',
      axisLabel: { color: darkTheme.textStyle?.color },
      splitLine: { lineStyle: { color: 'rgba(255,255,255,0.03)' } }
    },
    series: data.series.map((s, idx) => ({
      name: s.name,
      type: 'line',
      smooth: true,
      symbol: 'circle',
      symbolSize: 6,
      data: s.data,
      showSymbol: false,
      lineStyle: { width: 2 },
      color: colors[idx % colors.length],
      areaStyle: { opacity: 0.06 }
    }))
  }
}

/** stackedBarOption */
export function stackedBarOption(
  data: { x: string[]; series: Array<{ name: string; data: number[] }> },
  opts?: { title?: string; stacked?: boolean; colorPalette?: string[]; unit?: string; valueFormatter?: FormatterFn }
): EChartsOption {
  const colors = ensureColors(opts?.colorPalette)
  const valueFmt = opts?.valueFormatter ?? defaultValueFormatter(opts?.unit)
  return {
    backgroundColor: lightTheme.backgroundColor,
    title: { text: opts?.title ?? '', left: 'left', textStyle: { color: lightTheme.title?.textStyle?.color } },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const rows = params.map((p: any) => `${p.seriesName}: ${valueFmt(p.value)}`)
        return `${params[0].axisValue}<br/>${rows.join('<br/>')}`
      }
    },
    legend: { top: 8, textStyle: { color: lightTheme.textStyle?.color } },
    grid: { left: '6%', right: '6%', top: 48, bottom: 30 },
    xAxis: { type: 'category', data: data.x, axisLabel: { color: lightTheme.textStyle?.color } },
    yAxis: { type: 'value', axisLabel: { color: lightTheme.textStyle?.color } },
    series: data.series.map((s, idx) => ({
      name: s.name,
      type: 'bar',
      stack: opts?.stacked ? 'total' : undefined,
      data: s.data,
      color: colors[idx % colors.length],
      barMaxWidth: 36
    }))
  }
}

/** barWithLineOption */
export function barWithLineOption(
  data: { x: string[]; bars: number[]; line: number[] },
  opts?: {
    title?: string
    barName?: string
    lineName?: string
    colorPalette?: string[]
    unit?: string
    valueFormatter?: FormatterFn
  }
): EChartsOption {
  const colors = ensureColors(opts?.colorPalette)
  const valueFmt = opts?.valueFormatter ?? defaultValueFormatter(opts?.unit)
  return {
    backgroundColor: darkTheme.backgroundColor,
    title: { text: opts?.title ?? '', left: 'left', textStyle: { color: darkTheme.title?.textStyle?.color } },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params: any) => {
        const arr = params.map((p: any) => `${p.seriesName}: ${valueFmt(p.value)}`)
        return `${params[0].axisValue}<br/>${arr.join('<br/>')}`
      }
    },
    legend: { top: 8, textStyle: { color: darkTheme.textStyle?.color } },
    grid: { left: '6%', right: '6%', top: 48, bottom: 30 },
    xAxis: { type: 'category', data: data.x, axisLabel: { color: darkTheme.textStyle?.color } },
    yAxis: [
      { type: 'value', axisLabel: { color: darkTheme.textStyle?.color }, name: opts?.unit ?? '' },
      {
        type: 'value',
        name: '%',
        position: 'right',
        axisLabel: { color: darkTheme.textStyle?.color },
        min: 0,
        max: 150
      }
    ],
    series: [
      { name: opts?.barName ?? '总量', type: 'bar', data: data.bars, barMaxWidth: 30, color: colors[0] },
      {
        name: opts?.lineName ?? '增长率',
        type: 'line',
        yAxisIndex: 1,
        data: data.line,
        smooth: true,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: { width: 2 },
        color: colors[1]
      }
    ]
  }
}

/** groupedBarOption - 簇（分组）柱状统计图
 * @param {data} data x轴数据名，series 每一个柱状标识的数据名含义(name)，以及该数据名下的数据(data数组)
 * @param {opts.title} opts.title 统计图标题
 * @param {opts.colorPalette} opts.colorPalette 簇状每一个柱状颜色(非渐变色，需求有了之后再封装)
 * @param {opts.unit} opts.unit 统计图整体单位
 * @param {opts.valueFormatter} opts.valueFormatter 统计图自定义值格式
 * @param {opts.barMaxWidth} opts.barMaxWidth 统计图每一个柱状最大宽度
 * @param {opts.barCategoryGap} opts.barCategoryGap 统计图相邻柱状间隔
 * @param {opts.labelMinValue} opts.labelMinValue 统计图显示的label
 */
export function groupedBarOption(
  data: { x: string[]; series: Array<{ name: string; data: number[] }> },
  opts?: {
    title?: string
    colorPalette?: string[]
    unit?: string
    valueFormatter?: FormatterFn
    barMaxWidth?: number
    barCategoryGap?: string
    labelMinValue?: number // 小于该值不显示 label
  }
): EChartsOption {
  const colors = opts?.colorPalette ?? (lightTheme.color as string[]) ?? ensureColors(opts?.colorPalette)

  const valueFmt = opts?.valueFormatter ?? defaultValueFormatter(opts?.unit)
  const labelMinValue = opts?.labelMinValue ?? 1

  return {
    backgroundColor: lightTheme.backgroundColor,

    title: {
      text: opts?.title ?? '',
      left: 'left',
      top: 8,
      textStyle: {
        color: lightTheme.title?.textStyle?.color ?? '#333',
        fontSize: 14,
        fontWeight: 500
      }
    },

    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255,255,255,0.95)',
      borderColor: 'rgba(0,0,0,0.06)',
      borderWidth: 1,
      textStyle: { color: '#333', fontSize: 12 },
      formatter: (params: TopLevelFormatterParams) => {
        const rows = (params as CallbackDataParams[]).map(
          (p) => `${p.marker} ${p.seriesName}：${valueFmt(p.value as string)}`
        )

        return `
          <div style="font-weight:500;margin-bottom:4px;">
            ${(params as any[])[0]?.axisValue ?? ''}
          </div>
          ${rows.join('<br/>')}
        `
      }
    },

    legend: {
      bottom: 8,
      left: 'center',
      icon: 'roundRect',
      itemWidth: 10,
      itemHeight: 10,
      itemGap: 18,
      textStyle: {
        color: lightTheme.textStyle?.color ?? '#666',
        fontSize: 12
      },
      data: data.series.map((s) => s.name)
    },

    grid: {
      left: '3%',
      right: '3%',
      top: 56,
      bottom: '16%'
    },

    xAxis: {
      type: 'category',
      data: data.x,
      axisTick: { alignWithLabel: true },
      axisLine: {
        lineStyle: { color: 'rgba(0,0,0,0.08)' }
      },
      axisLabel: {
        color: lightTheme.textStyle?.color ?? '#666',
        fontSize: 12
      }
    },

    yAxis: {
      type: 'value',
      axisLabel: {
        color: lightTheme.textStyle?.color ?? '#666',
        fontSize: 12
      },
      axisLine: {
        show: false
      },
      splitLine: {
        lineStyle: {
          type: 'dashed',
          color: 'rgba(0,0,0,0.05)'
        }
      }
    },

    barCategoryGap: opts?.barCategoryGap ?? '30%',

    series: data.series.map((s, idx) => ({
      name: s.name,
      type: 'bar',
      data: s.data,
      barMaxWidth: opts?.barMaxWidth ?? 22,
      itemStyle: {
        color: colors[idx % colors.length],
        borderRadius: [4, 4, 0, 0]
      },
      emphasis: {
        focus: 'series'
      },
      label: {
        show: false,
        position: 'top',
        fontSize: 11,
        color: '#333',
        formatter: ({ value }: any) => (value >= labelMinValue ? valueFmt(value) : '')
      }
    }))
  }
}

/** multiLineAreaOption - 多折线面积统计图（支持双Y轴）
 * @param {data} data x轴标签数据，series 每个系列的数据配置
 * @param {opts.title} opts.title 统计图标题
 * @param {opts.unit} opts.unit 统计图整体单位（可被系列自定义单位覆盖）
 * @param {opts.valueFormatter} opts.valueFormatter 统计图自定义值格式函数
 * @param {opts.smooth} opts.smooth 是否平滑曲线（默认true）
 * @param {opts.yAxis} opts.yAxis Y轴配置数组，支持多Y轴
 */
export function multiLineAreaOption(
  data: {
    x: string[]
    series: Array<{
      name: string
      data: (number | string)[]
      color?: string
      areaOpacity?: number
      yAxisIndex?: number
    }>
  },
  opts?: {
    title?: string
    unit?: string
    valueFormatter?: FormatterFn
    smooth?: boolean
    yAxis?: Array<{
      name?: string
      position?: 'left' | 'right'
      axisLabel?: {
        formatter?: string | ((value: number) => string)
      }
      splitLine?: {
        show?: boolean
      }
    }>
  }
): EChartsOption {
  const colors = ensureColors()
  const valueFmt = opts?.valueFormatter ?? defaultValueFormatter(opts?.unit)

  return {
    backgroundColor: 'transparent',
    tooltip: {
      trigger: 'axis',
      formatter: (params: any) => {
        const rows = params.map((p: any) => {
          const value = opts?.unit ? `${valueFmt(p.value)}` : valueFmt(p.value)
          return `${p.marker}${p.seriesName}: ${value}`
        })
        return `${params[0].axisValue}<br/>${rows.join('<br/>')}`
      }
    },
    legend: {
      data: data.series.map((s) => s.name),
      left: 'center',
      itemWidth: 20,
      itemHeight: 15,
      itemGap: 18,
      bottom: 10,
      textStyle: { color: '#666' }
    },
    grid: {
      left: '3%',
      right: opts?.yAxis && opts.yAxis.length > 1 ? '10%' : '3%',
      bottom: '15%',
      top: opts?.title ? 50 : 20
    },
    xAxis: {
      type: 'category',
      data: data.x,
      boundaryGap: false,
      axisLine: {
        lineStyle: {
          color: '#666'
        }
      },
      axisLabel: {
        color: '#666'
      }
    },
    yAxis:
      opts?.yAxis && opts.yAxis.length > 0
        ? opts.yAxis.map((axis, index) => ({
            type: 'value',
            name: axis.name || '',
            position: axis.position || (index === 0 ? 'left' : 'right'),
            axisLine: {
              lineStyle: {
                color: '#666'
              }
            },
            axisLabel: {
              color: '#666',
              formatter: axis.axisLabel?.formatter || ((val: number) => valueFmt(val))
            },
            splitLine: axis.splitLine || {
              show: index === 0,
              lineStyle: {
                color: 'rgba(0,0,0,0.05)'
              }
            }
          }))
        : [
            {
              type: 'value',
              axisLabel: {
                color: '#666',
                formatter: (val: number) => valueFmt(val)
              },
              splitLine: {
                lineStyle: {
                  color: 'rgba(0,0,0,0.05)'
                }
              }
            }
          ],
    series: data.series.map((s, idx) => {
      const color = s.color || colors[idx % colors.length]
      const areaOpacity = s.areaOpacity ?? 0.8

      return {
        name: s.name,
        type: 'line',
        smooth: opts?.smooth ?? true,
        symbol: 'none',
        symbolSize: 6,
        data: s.data,
        yAxisIndex: s.yAxisIndex || 0,
        lineStyle: {
          width: 2,
          color: color
        },
        itemStyle: {
          color: color
        },
        areaStyle: {
          origin: 'start',
          opacity: areaOpacity,
          color:
            color +
            Math.round(areaOpacity * 255)
              .toString(16)
              .padStart(2, '0')
        }
      }
    }),
    ...(opts?.title
      ? {
          title: {
            text: opts.title,
            left: 'left',
            top: 10,
            textStyle: {
              color: '#333',
              fontSize: 16,
              fontWeight: 400
            }
          }
        }
      : {})
  }
}

/** barLineChartOption - 折柱混合图（柱状图+折线图，双Y轴）
 * @param {data} data x轴数据名，series 每一个系列的数据配置
 * @param {opts.title} opts.title 统计图标题
 * @param {opts.colorPalette} opts.colorPalette 柱状图和折线图的颜色数组
 * @param {opts.unit} opts.unit 统计图整体单位（主要用于柱状图）
 * @param {opts.lineUnit} opts.lineUnit 折线图单位（默认%）
 * @param {opts.valueFormatter} opts.valueFormatter 统计图自定义值格式
 * @param {opts.barMaxWidth} opts.barMaxWidth 柱状图最大宽度
 * @param {opts.labelMinValue} opts.labelMinValue 柱状图显示的label最小值
 * @param {opts.smooth} opts.smooth 折线图是否平滑（默认true）
 * @param {opts.barGradient} opts.barGradient 柱状图渐变
 */
export function barLineChartOption(
  data: {
    x: string[]
    series: Array<{
      name: string
      data: number[]
    }>
  },
  opts?: {
    title?: string
    colorPalette?: string[]
    unit?: string
    lineUnit?: string
    valueFormatter?: FormatterFn
    lineValueFormatter?: FormatterFn
    barMaxWidth?: number
    labelMinValue?: number
    smooth?: boolean
    barGradient?: {
      top?: string // 顶部颜色
      bottom?: string // 底部颜色
    }
  }
): EChartsOption {
  const colors = opts?.colorPalette ?? (lightTheme.color as string[]) ?? ensureColors(opts?.colorPalette)
  const valueFmt = opts?.valueFormatter ?? defaultValueFormatter(opts?.unit)
  const lineValueFmt = opts?.lineValueFormatter ?? defaultValueFormatter(opts?.lineUnit || '%')
  const labelMinValue = opts?.labelMinValue ?? 1
  // 柱状图渐变颜色
  const barTopColor = opts?.barGradient?.top || colors[0 % colors.length]
  const barBottomColor = opts?.barGradient?.bottom || colors[0 % colors.length]
  return {
    backgroundColor: lightTheme.backgroundColor,

    title: {
      text: opts?.title ?? '',
      left: 'left',
      top: 8,
      textStyle: {
        color: lightTheme.title?.textStyle?.color ?? '#333',
        fontSize: 14,
        fontWeight: 500
      }
    },

    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      backgroundColor: 'rgba(255,255,255,0.95)',
      borderColor: 'rgba(0,0,0,0.06)',
      borderWidth: 1,
      textStyle: { color: '#333', fontSize: 12 },
      formatter: (params: TopLevelFormatterParams) => {
        const rows = (params as CallbackDataParams[]).map((p) => {
          const isBar = p.seriesIndex === 0
          const formatter = isBar ? valueFmt : lineValueFmt

          let marker = ''
          if (p.seriesIndex === 0) {
            marker = p.marker ? String(p.marker) : ''
          } else if (p.seriesIndex === 1) {
            const color = colors[1 % colors.length] || '#2CA5FF'
            marker = `<span style="display:inline-block;width:8px;height:8px;background-color:${color};border-radius:50%;margin-right:6px;"></span>`
          }

          return `${marker}${p.seriesName}：${formatter(p.value as string)}`
        })

        return `
			<div style="font-weight:500;margin-bottom:4px;">
				${(params as any[])[0]?.axisValue ?? ''}
			</div>
			${rows.join('<br/>')}
		`
      }
    },

    legend: {
      bottom: 8,
      left: 'center',
      itemWidth: 20,
      itemHeight: 15,
      itemGap: 18,
      textStyle: {
        color: lightTheme.textStyle?.color ?? '#666',
        fontSize: 12
      },
      data: data.series.map((s, index) => ({
        name: s.name,
        icon: index === 0 ? 'roundRect' : '' // 第一个柱状图用矩形，第二个折线图用默认
      }))
    },

    grid: {
      left: '3%',
      right: '3%',
      top: 56,
      bottom: '16%',
      containLabel: true
    },

    xAxis: {
      type: 'category',
      data: data.x,
      axisTick: { alignWithLabel: true },
      axisLine: {
        lineStyle: { color: 'rgba(0,0,0,0.08)' }
      },
      axisLabel: {
        color: lightTheme.textStyle?.color ?? '#666',
        fontSize: 12
      }
    },

    yAxis: [
      // 左侧Y轴（柱状图）
      {
        type: 'value',
        name: opts?.unit || '',
        nameTextStyle: {
          color: lightTheme.textStyle?.color ?? '#666',
          fontSize: 12
        },
        axisLabel: {
          color: lightTheme.textStyle?.color ?? '#666',
          fontSize: 12,
          formatter: (val: number) => valueFmt(val)
        },
        axisLine: {
          show: false
        },
        splitLine: {
          lineStyle: {
            type: 'dashed',
            color: 'rgba(0,0,0,0.05)'
          }
        }
      },
      // 右侧Y轴（折线图）
      {
        type: 'value',
        name: opts?.lineUnit || '',
        nameTextStyle: {
          color: lightTheme.textStyle?.color ?? '#666',
          fontSize: 12
        },
        position: 'right',
        axisLabel: {
          color: lightTheme.textStyle?.color ?? '#666',
          fontSize: 12,
          formatter: (val: number) => lineValueFmt(val)
        },
        axisLine: {
          show: false
        },
        splitLine: {
          show: false
        }
      }
    ],

    series: [
      // 柱状图
      {
        name: data.series[0]?.name || '柱状图',
        type: 'bar',
        data: data.series[0]?.data || [],
        barMaxWidth: opts?.barMaxWidth ?? 22,
        itemStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: barTopColor }, // 顶部颜色
              { offset: 1, color: barBottomColor } // 底部颜色
            ]
          },
          borderRadius: [10, 10, 10, 10]
        },
        emphasis: {
          focus: 'series'
        },
        label: {
          show: false,
          position: 'top',
          fontSize: 11,
          color: '#333',
          formatter: ({ value }: any) => (value >= labelMinValue ? valueFmt(value) : '')
        }
      },
      // 折线图
      {
        name: data.series[1]?.name || '折线图',
        type: 'line',
        data: data.series[1]?.data || [],
        yAxisIndex: 1,
        smooth: opts?.smooth ?? false,
        symbol: 'circle',
        symbolSize: 6,
        lineStyle: {
          width: 2,
          color: colors[1 % colors.length]
        },
        itemStyle: {
          color: '#fff',
          borderColor: colors[1 % colors.length],
          borderWidth: 1
        }
      }
    ],
    ...(opts?.title
      ? {
          title: {
            text: opts.title,
            left: 'left',
            top: 10,
            textStyle: {
              color: '#333',
              fontSize: 16,
              fontWeight: 400
            }
          }
        }
      : {})
  }
}

/** semiDonutOption - 扇形半圆图（环状半圆）
 * @param {data} data 每个扇形的数据配置
 * @param {opts.title} opts.title 统计图标题
 * @param {opts.total} opts.total 总销量数据
 * @param {opts.colorPalette} opts.colorPalette 扇形颜色数组
 * @param {opts.valueFormatter} opts.valueFormatter 统计图自定义值格式
 * @param {opts.unit} opts.unit 统计图整体单位
 * @param {opts.showTotal} opts.showTotal 是否显示中心总数（默认true）
 * @param {centerTitle} centerTitle  中心标题文本
 * @param {opts.radius} opts.radius 内外半径配置
 */
export function semiDonutOption(
  data: Array<{
    name: string
    value: number
    color?: string
  }>,
  opts?: {
    title?: string
    total?: number // 总销量数据
    colorPalette?: string[]
    valueFormatter?: FormatterFn
    unit?: string
    showTotal?: boolean
    centerTitle?: string // 中心标题文本
    radius?: [string, string] // 内外半径，如 ['40%', '70%']
  }
): EChartsOption {
  const colors = opts?.colorPalette ?? (lightTheme.color as string[]) ?? ensureColors(opts?.colorPalette)
  const valueFmt = opts?.valueFormatter ?? defaultValueFormatter(opts?.unit)
  const showTotal = opts?.showTotal ?? true
  const total = opts?.total ?? data.reduce((sum, item) => sum + item.value, 0)
  const radius = opts?.radius ?? ['70%', '90%']

  // 生成半圆数据（180度）
  const halfCircleData = data.map((item, idx) => ({
    name: item.name,
    value: item.value,
    itemStyle: {
      color: item.color || colors[idx % colors.length]
    }
  }))

  return {
    backgroundColor: lightTheme.backgroundColor,
    title: [
      {
        text: opts?.title ?? '商品销量',
        left: 'left',
        top: 10,
        textStyle: {
          color: lightTheme.title?.textStyle?.color ?? '#333',
          fontSize: 16,
          fontWeight: 'normal'
        }
      },
      // 环状中间显示数据
      {
        text: `${opts?.centerTitle || '商品总销量'}\n${valueFmt(total)}\n${opts?.unit || ''}`,
        left: '350',
        top: '160',
        textAlign: 'center',
        textVerticalAlign: 'middle',
        textStyle: {
          color: lightTheme.title?.textStyle?.color ?? '#333',
          fontSize: 18,
          fontWeight: 'normal',
          lineHeight: 24
        }
      }
    ],
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(255,255,255,0.95)',
      borderColor: 'rgba(0,0,0,0.06)',
      borderWidth: 1,
      textStyle: { color: '#333', fontSize: 12 },
      formatter: (params: any) => {
        const percent = ((params.percent as number) || 0).toFixed(1)
        // 使用 ECharts 内置的 marker（带颜色的小圆点）
        return `${params.marker} ${params.name}<br/>
				数量: ${valueFmt(params.value)}${opts?.unit || ''}<br/>
				占比: ${percent}%`
      }
    },
    legend: {
      show: data.length > 1,
      bottom: 50,
      itemWidth: 20,
      itemHeight: 15,
      //80%宽度时自动换行
      width: '80%',
      itemGap: 18,
      left: 'center',
      itemStyle: {
        borderWidth: 0
      },
      textStyle: {
        color: lightTheme.textStyle?.color ?? '#666',
        fontSize: 12
      },
      data: data.map((item) => item.name)
    },
    series: [
      {
        name: '销量',
        type: 'pie',
        radius: radius,
        center: ['50%', showTotal ? '60%' : '50%'], // 半圆需要向下偏移
        startAngle: 180, // 起始角度180度
        endAngle: 0, // 结束角度0度，形成半圆
        avoidLabelOverlap: false,
        itemStyle: {
          borderRadius: 0, // 扇形圆角
          borderColor: lightTheme.backgroundColor,
          borderWidth: 2
        },
        label: {
          show: true,
          position: 'outside',
          formatter: (params: any) => {
            return `${params.percent}%\n${params.value}`
          },
          color: '#000000ff',
          fontSize: '12'
        },
        emphasis: {
          scale: true,
          scaleSize: 10,
          itemStyle: {
            shadowBlur: 10,
            shadowOffsetX: 0,
            shadowColor: 'rgba(0, 0, 0, 0.5)'
          },
          label: {
            show: true,
            fontWeight: 'bold'
          }
        },
        labelLine: {
          length: 10,
          length2: 10,
          smooth: true
        },
        data: halfCircleData
      }
    ]
  }
}

/** 横向单指标条形图 */
export function singleHorizontalBarOption(
  items: SingleBarItem[],
  cfg: {
    title?: string
    sortBy?: 'value' | 'none'
    sortDesc?: boolean
    barHeight?: number

    series: {
      name: string
      unit?: string
      max?: number
      formatter?: FormatterFn
      color?: string
      showLabel?: boolean
    }

    showLegend?: boolean
  }
): EChartsOption {
  const { sortBy = 'value', sortDesc = true, barHeight = 16 } = cfg

  const valueFmt = cfg.series.formatter ?? defaultValueFormatter(cfg.series.unit)

  const list = [...items].sort((a, b) => {
    if (sortBy === 'none') return 0
    const diff = a.value - b.value
    return sortDesc ? -diff : diff
  })

  const labels = list.map((i) => i.label)
  const values = list.map((i) => i.value)

  const maxValue = values.length ? Math.max(...values) : 0
  const xMax = cfg.series.max ?? (maxValue > 0 ? maxValue * 1.15 : 1)

  return {
    backgroundColor: lightTheme.backgroundColor,
    title: cfg.title
      ? {
          text: cfg.title,
          left: 'left',
          top: 8,
          textStyle: {
            fontSize: 14,
            fontWeight: 500
          }
        }
      : undefined,

    grid: {
      left: 140,
      right: 40,
      top: cfg.title ? 56 : 24,
      bottom: 48
    },

    legend: cfg.showLegend
      ? {
          bottom: 8,
          data: [cfg.series.name]
        }
      : undefined,

    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const ps = params as CallbackDataParams[]
        const p = ps[0]
        return `${p.name}<br/>${cfg.series.name}: ${valueFmt(p.value as number)}`
      }
    },

    yAxis: {
      type: 'category',
      inverse: true,
      data: labels,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#333',
        fontSize: 12
      }
    },

    xAxis: {
      type: 'value',
      min: 0,
      max: xMax,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        formatter: (v) => valueFmt(v)
      }
    },

    series: [
      {
        name: cfg.series.name,
        type: 'bar',
        barWidth: barHeight,
        data: values,
        itemStyle: {
          color: cfg.series.color ?? '#6CC644',
          borderRadius: [0, 6, 6, 0]
        },
        label: cfg.series.showLabel
          ? {
              show: true,
              position: 'insideRight',
              formatter: (p) => valueFmt(p.value as number),
              color: '#fff'
            }
          : undefined
      }
    ]
  }
}

/** 横向双指标对齐条形图
 * @param {items} items 横向双指标数据配置
 * @param {cfg.title} cfg.title 统计图标题
 * @param {cfg.sortBy} cfg.sortBy 排序左边、右边
 * @param {cfg.sortDesc} cfg.sortDesc 是否排序详情
 * @param {cfg.barHeight} cfg.barHeight 柱状图高度
 * @param {cfg.left.name} cfg.left.name 统计图左侧系列名
 * @param {cfg.left.unit} cfg.left.unit 统计图左侧系列单位
 * @param {cfg.left.max} cfg.left.max 统计图左侧系列单位最大值
 * @param {cfg.left.formatter} cfg.left.formatter 统计图左侧系列单位文字格式
 * @param {cfg.left.color} cfg.left.color 统计图左侧柱状颜色
 * @param {cfg.left.bgColor} cfg.left.bgColor 统计图左侧背景颜色
 * @param {cfg.left.showLabel} cfg.left.showLabel 统计图左侧是否展示标题
 * @param {cfg.right.name} cfg.right.name 统计图右侧系列名
 * @param {cfg.right.unit} cfg.right.unit 统计图右侧系列单位
 * @param {cfg.right.max} cfg.right.max 统计图右侧系列单位最大值
 * @param {cfg.right.formatter} cfg.right.formatter 统计图右侧系列单位文字格式
 * @param {cfg.right.color} cfg.right.color 统计图右侧柱状颜色
 * @param {cfg.right.bgColor} cfg.right.bgColor 统计图右侧背景颜色
 * @param {cfg.right.showLabel} cfg.right.showLabel 统计图右侧是否展示标题
 */
export function dualHorizontalBarOption(
  items: DualBarItem[],
  cfg: {
    title?: string
    sortBy?: 'left' | 'right' | 'none'
    sortDesc?: boolean
    barHeight?: number

    left: {
      name: string
      unit?: string
      max?: number
      formatter?: FormatterFn
      color?: string
      bgColor?: string
      showLabel?: boolean
    }

    right: {
      name: string
      max?: number
      formatter?: FormatterFn
      color?: (v: number) => string
      showLabel?: boolean
    }
  }
): EChartsOption {
  const { sortBy = 'left', sortDesc = true, barHeight = 16 } = cfg

  const leftFmt = cfg.left.formatter ?? defaultValueFormatter(cfg.left.unit)
  const rightFmt = cfg.right.formatter ?? ((v: number) => `${v}`)

  /** 排序 */
  const list = [...items].sort((a, b) => {
    if (sortBy === 'none') return 0
    const diff = a[sortBy] - b[sortBy]
    return sortDesc ? -diff : diff
  })

  const labels = list.map((i) => i.label)
  const leftValues = list.map((i) => i.left)
  const rightValues = list.map((i) => -i.right) // 👈 核心：负值向左

  const leftMax = cfg.left.max ?? Math.max(...leftValues) * 1.15
  const rightMax = cfg.right.max ?? Math.max(...list.map((i) => i.right)) * 1.15

  return {
    backgroundColor: lightTheme.backgroundColor,
    title: cfg.title
      ? {
          text: cfg.title,
          left: 'left',
          top: 8,
          textStyle: {
            fontSize: 14,
            fontWeight: 500
          }
        }
      : undefined,

    grid: {
      left: 140,
      right: 140, // 👈 负值必须留空间
      top: cfg.title ? 56 : 24,
      bottom: 48
    },

    legend: {
      bottom: 8,
      data: [cfg.left.name, cfg.right.name]
    },

    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'shadow' },
      formatter: (params) => {
        const ps = params as CallbackDataParams[]
        const name = ps[0].name
        const l = ps.find((p) => p.seriesName === cfg.left.name)
        const r = ps.find((p) => p.seriesName === cfg.right.name)

        return `
          <b>${name}</b><br/>
          ${cfg.left.name}：${leftFmt(l?.value as number)}<br/>
          ${cfg.right.name}：${rightFmt(Math.abs(r?.value as number))}
        `
      }
    },

    yAxis: {
      type: 'category',
      inverse: true,
      data: labels,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        color: '#333',
        fontSize: 12
      }
    },

    xAxis: {
      type: 'value',
      min: -rightMax,
      max: leftMax,
      axisLine: { show: false },
      axisTick: { show: false },
      splitLine: { show: false },
      axisLabel: {
        formatter: (v) => (v < 0 ? rightFmt(Math.abs(v)) : leftFmt(v))
      }
    },

    series: [
      /** 左指标（向右） */
      {
        name: cfg.left.name,
        type: 'bar',
        barWidth: barHeight,
        barGap: '40%',
        data: leftValues,
        itemStyle: {
          color: cfg.left.color ?? '#6CC644',
          borderRadius: [0, 6, 6, 0]
        },
        label: cfg.left.showLabel
          ? {
              show: true,
              position: 'insideRight',
              formatter: (p) => leftFmt(p.value as number),
              color: '#fff'
            }
          : undefined
      },

      /** 右指标（向左） */
      {
        name: cfg.right.name,
        type: 'bar',
        barWidth: barHeight,
        barGap: '40%',
        data: rightValues,
        itemStyle: {
          color:
            typeof cfg.right.color === 'function'
              ? (p) => cfg.right.color!(Math.abs(p.value as number))
              : (cfg.right.color ?? '#0A84FF'),
          borderRadius: [6, 0, 0, 6]
        },
        label: cfg.right.showLabel
          ? {
              show: true,
              position: 'insideLeft',
              formatter: (p) => rightFmt(Math.abs(p.value as number)),
              color: '#fff'
            }
          : undefined
      }
    ]
  }
}

/** 四象限气泡散点图
 * @param {data} data 每个气泡的数据配置
 * @param {cfg.title} cfg.title 统计图标题
 * @param {cfg.x} cfg.x 统计图x轴数据
 * @param {cfg.y} cfg.y 统计图x轴数据
 * @param {cfg.size} cfg.size 统计图数据限定数据范围
 * @param {cfg.split} cfg.split 统计图数据数据分割
 * @param {quadrantMap} quadrantMap 统计图四象限设定
 */
export function quadrantBubbleOption(
  data: QuadrantBubbleItem[],
  cfg: QuadrantConfig,
  quadrantMap: QuadrantMap
): EChartsOption {
  const xSplit = cfg.split?.x ?? average(data.map((d) => d.x))
  const ySplit = cfg.split?.y ?? average(data.map((d) => d.y))

  const xFmt = cfg.x.formatter ?? ((v) => `${v}`)
  const yFmt = cfg.y.formatter ?? ((v) => `${v}`)

  const sizeMin = cfg.size?.min ?? 20
  const sizeMax = cfg.size?.max ?? 60

  /** 四象限颜色 */

  /** 按象限分组 */

  const seriesMap: Record<string, any[]> = {
    LL: [],
    LH: [],
    HL: [],
    HH: []
  }

  data.forEach((d) => {
    const key = d.x >= xSplit ? (d.y >= ySplit ? 'HH' : 'HL') : d.y >= ySplit ? 'LH' : 'LL'

    seriesMap[key].push(d)
  })

  const series: any = Object.entries(seriesMap).map(([key, items]) => ({
    name: quadrantMap[key as keyof typeof quadrantMap].name,
    type: 'scatter',
    symbolSize: (val: number[]) => {
      const size = val[2]
      const max = Math.max(...data.map((d) => d.size))
      return sizeMin + (size / max) * (sizeMax - sizeMin)
    },
    data: items.map((i) => [i.x, i.y, i.size, i.name]),
    itemStyle: {
      color: quadrantMap[key as keyof typeof quadrantMap].color,
      opacity: 0.75
    },
    label: {
      show: true,
      // @ts-expect-error
      formatter: (p) => `${(p.data as any[])[3]}\n${(p.data as any[])[2]}`,
      color: '#333',
      fontSize: 11
    }
  }))

  return {
    backgroundColor: lightTheme.backgroundColor,

    title: cfg.title
      ? {
          text: cfg.title,
          left: 'left',
          top: 8
        }
      : undefined,

    tooltip: {
      formatter: (p) => {
        const [x, y, size, name] = (p as CallbackDataParams).data as any[]
        return `
					<b>${name}</b><br/>
					${cfg.x.name}：${xFmt(x)}<br/>
					${cfg.y.name}：${yFmt(y)}<br/>
					规模：${size}
				`
      }
    },

    grid: {
      left: 60,
      right: 40,
      top: cfg.title ? 48 : 24,
      bottom: 60
    },

    xAxis: {
      name: cfg.x.name,
      splitLine: { show: false }
    },

    yAxis: {
      name: cfg.y.name,
      splitLine: { show: false }
    },

    /** 象限分割线 */
    graphic: [
      {
        type: 'line',
        shape: {
          x1: '50%',
          y1: '0%',
          x2: '50%',
          y2: '100%'
        },
        style: {
          stroke: '#ddd',
          lineWidth: 1,
          lineDash: [4, 4]
        }
      },
      {
        type: 'line',
        shape: {
          x1: '0%',
          y1: '50%',
          x2: '100%',
          y2: '50%'
        },
        style: {
          stroke: '#ddd',
          lineWidth: 1,
          lineDash: [4, 4]
        }
      }
    ],

    legend: {
      bottom: 10
    },

    series
  }
}

export interface DesignLineAreaSeriesItem {
  name: string
  data: number[]
  color: string
  areaStartColor?: string
  areaEndColor?: string
}

export interface DesignLineAreaOptionParams {
  x: string[]
  series: DesignLineAreaSeriesItem[]
}

export interface DesignLineAreaOptionExtra {
  title?: string
  yAxisName?: string
  yAxisUnit?: string
  legendTop?: number
  gridTop?: number
}

/**
 * 设计稿同款双折线面积图
 * 适用于：订单量统计、利润率趋势、毛利率趋势等
 */
export function designLineAreaOption(
  data: DesignLineAreaOptionParams,
  extra: DesignLineAreaOptionExtra = {}
): EChartsOption {
  const { title = '', yAxisName = '', yAxisUnit = '', legendTop = 12, gridTop = 58 } = extra

  return {
    backgroundColor: 'transparent',

    title: {
      text: title,
      left: 0,
      top: 0,
      textStyle: {
        color: '#333333',
        fontSize: 14,
        fontWeight: 500
      }
    },

    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255, 255, 255, 0.96)',
      borderColor: '#E5EAF3',
      borderWidth: 1,
      textStyle: {
        color: '#333333',
        fontSize: 12
      },
      axisPointer: {
        type: 'line',
        lineStyle: {
          color: '#BFDFFF',
          type: 'dashed'
        }
      },
      formatter: (params) => {
        const list = Array.isArray(params) ? params : [params]
        const axisValue = list[0]?.axisValueLabel || list[0]?.name || ''

        let html = `<div style="font-size:12px;font-weight:600;margin-bottom:6px;color:#333;">${axisValue}</div>`

        list.forEach((item: any) => {
          const value = item.value ?? 0
          const color = item.color

          html += `
						<div style="display:flex;align-items:center;justify-content:space-between;gap:16px;margin:3px 0;">
							<span style="display:flex;align-items:center;color:#333;">
								<span style="width:8px;height:8px;border-radius:50%;background:${color};margin-right:6px;"></span>
								${item.seriesName}
							</span>
							<span style="font-weight:600;color:${color};">${value}${yAxisUnit}</span>
						</div>
					`
        })

        return html
      }
    },

    legend: {
      top: legendTop,
      left: 'center',
      itemWidth: 20,
      itemHeight: 10,
      icon: 'roundRect',
      textStyle: {
        color: '#333333',
        fontSize: 12
      },
      data: data.series.map((item) => item.name)
    },

    grid: {
      left: 8,
      right: 16,
      top: gridTop,
      bottom: 8,
      containLabel: true
    },

    xAxis: {
      type: 'category',
      boundaryGap: false,
      data: data.x,
      axisTick: {
        show: false
      },
      axisLine: {
        lineStyle: {
          color: '#DDE4F0'
        }
      },
      axisLabel: {
        color: '#666666',
        fontSize: 12
      }
    },

    yAxis: {
      type: 'value',
      name: yAxisName,
      min: 0,
      splitNumber: 5,
      axisTick: {
        show: false
      },
      axisLine: {
        show: false
      },
      axisLabel: {
        color: '#666666',
        fontSize: 12,
        formatter: yAxisUnit ? `{value}${yAxisUnit}` : '{value}'
      },
      splitLine: {
        lineStyle: {
          color: '#E9EEF5',
          type: 'dashed'
        }
      }
    },

    series: data.series.map((item) => {
      return {
        name: item.name,
        type: 'line',
        smooth: true,
        symbol: 'none',
        data: item.data,
        lineStyle: {
          width: 2,
          color: item.color
        },
        itemStyle: {
          color: item.color
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              {
                offset: 0,
                color: item.areaStartColor || item.color
              },
              {
                offset: 1,
                color: item.areaEndColor || 'rgba(255,255,255,0)'
              }
            ]
          }
        },
        emphasis: {
          focus: 'series'
        }
      }
    })
  }
}

/** ===================== BI大屏专用：销售商户排名 ===================== */

export interface MerchantSalesRankItem {
  name: string
  value: number
  /** 支持单色或渐变双色 */
  color?: string | [string, string]
}

export interface MerchantSalesRankOptionConfig {
  title?: string
  unit?: string
  max?: number
  barWidth?: number
  sortDesc?: boolean
  showValueLabel?: boolean
  nameWidth?: number
  rightPadding?: number
  palette?: Array<string | [string, string]>
  backgroundColor?: string
  trackColor?: string
  dotColor?: string
  valueFormatter?: FormatterFn
}

function toRgba(color: string, alpha: number) {
  if (!color) return `rgba(255,255,255,${alpha})`
  if (color.startsWith('rgba(')) {
    return color.replace(/rgba\(([^)]+),[^,]+\)$/, `rgba($1, ${alpha})`)
  }
  if (color.startsWith('rgb(')) {
    return color.replace('rgb(', 'rgba(').replace(')', `, ${alpha})`)
  }
  const hex = color.replace('#', '')
  if (![3, 6].includes(hex.length)) return color

  const full =
    hex.length === 3
      ? hex
          .split('')
          .map((s) => s + s)
          .join('')
      : hex
  const r = parseInt(full.slice(0, 2), 16)
  const g = parseInt(full.slice(2, 4), 16)
  const b = parseInt(full.slice(4, 6), 16)
  return `rgba(${r}, ${g}, ${b}, ${alpha})`
}

function darkenHex(hex: string, amount = 0.28) {
  if (!hex || !hex.startsWith('#')) return hex
  const full = hex.length === 4 ? `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}` : hex
  const r = parseInt(full.slice(1, 3), 16)
  const g = parseInt(full.slice(3, 5), 16)
  const b = parseInt(full.slice(5, 7), 16)

  const clamp = (n: number) => Math.max(0, Math.min(255, Math.round(n)))
  const rr = clamp(r * (1 - amount))
  const gg = clamp(g * (1 - amount))
  const bb = clamp(b * (1 - amount))

  return `#${rr.toString(16).padStart(2, '0')}${gg.toString(16).padStart(2, '0')}${bb.toString(16).padStart(2, '0')}`
}

function buildGradientColor(color?: string | [string, string], fallback?: string | [string, string]) {
  const source = color ?? fallback ?? '#2ab1ff'

  if (Array.isArray(source)) {
    return {
      type: 'linear',
      x: 0,
      y: 0,
      x2: 1,
      y2: 0,
      colorStops: [
        { offset: 0, color: source[0] },
        { offset: 1, color: source[1] }
      ]
    }
  }

  return {
    type: 'linear',
    x: 0,
    y: 0,
    x2: 1,
    y2: 0,
    colorStops: [
      { offset: 0, color: source },
      { offset: 1, color: toRgba(source, 0.65) }
    ]
  }
}

export function merchantSalesRankOption(
  items: MerchantSalesRankItem[],
  cfg?: MerchantSalesRankOptionConfig
): EChartsOption {
  const {
    title = '',
    unit,
    barWidth = 10,
    sortDesc = true,
    showValueLabel = true,
    nameWidth = 88,
    rightPadding = 34,
    backgroundColor = 'transparent',
    trackColor = 'rgba(190, 213, 255, 0.18)',
    dotColor = '#ffffff'
  } = cfg ?? {}

  const valueFmt = cfg?.valueFormatter ?? defaultValueFormatter(unit)
  const fallbackPalette =
    cfg?.palette ??
    ([
      ['#ffe600', '#fff264'],
      ['#b791ff', '#d4c6ff'],
      ['#ff8d4b', '#ffb17c'],
      ['#2ab1ff', '#59ddff'],
      ['#2e97ff', '#53d8ff'],
      ['#3399ff', '#54ddff'],
      ['#3796ff', '#5adfff']
    ] as Array<string | [string, string]>)

  const list = [...items].sort((a, b) => (sortDesc ? b.value - a.value : a.value - b.value))
  const labels = list.map((item) => item.name)
  const values = list.map((item) => item.value)
  const maxValue = cfg?.max ?? (values.length ? Math.max(...values) : 0)
  const safeMax = maxValue > 0 ? maxValue : 1
  const trackValues = values.map(() => safeMax * 1.04)

  return {
    backgroundColor,
    animation: false,
    grid: {
      left: 18,
      right: rightPadding,
      top: title ? 44 : 12,
      bottom: 12
    },
    title: title
      ? {
          text: title,
          left: 'left',
          top: 8,
          textStyle: {
            color: darkTheme.title?.textStyle?.color ?? '#eef8ff',
            fontSize: 16,
            fontWeight: 600
          }
        }
      : undefined,
    xAxis: {
      type: 'value',
      max: safeMax * 1.12,
      show: false
    },
    yAxis: {
      type: 'category',
      inverse: true,
      data: labels,
      axisLine: { show: false },
      axisTick: { show: false },
      axisLabel: {
        interval: 0,
        margin: 16,
        color: darkTheme.textStyle?.color ?? '#eef8ff',
        formatter: (value: string, index: number) => {
          const rankStyle = index === 0 ? 'rank1' : index === 1 ? 'rank2' : index === 2 ? 'rank3' : 'rank'
          return `{${rankStyle}|${index + 1}} {name|${value}}`
        },
        rich: {
          rank1: {
            width: 26,
            height: 26,
            align: 'center',
            verticalAlign: 'middle',
            borderRadius: 6,
            color: '#7e6100',
            fontWeight: 800,
            backgroundColor: '#ffe600'
          },
          rank2: {
            width: 26,
            height: 26,
            align: 'center',
            verticalAlign: 'middle',
            borderRadius: 6,
            color: '#ffffff',
            fontWeight: 800,
            backgroundColor: '#b8bfce'
          },
          rank3: {
            width: 26,
            height: 26,
            align: 'center',
            verticalAlign: 'middle',
            borderRadius: 6,
            color: '#ffffff',
            fontWeight: 800,
            backgroundColor: '#ff7a1a'
          },
          rank: {
            width: 26,
            height: 26,
            align: 'center',
            verticalAlign: 'middle',
            borderRadius: 6,
            color: '#ffffff',
            fontWeight: 800,
            backgroundColor: '#2c76ff'
          },
          name: {
            width: nameWidth,
            padding: [0, 0, 0, 10],
            color: '#eef8ff',
            fontWeight: 700,
            fontSize: 15
          }
        }
      }
    },
    tooltip: {
      trigger: 'axis',
      axisPointer: { type: 'none' },
      backgroundColor: 'rgba(5, 30, 88, 0.94)',
      borderColor: 'rgba(86, 215, 255, 0.35)',
      textStyle: { color: '#ffffff' },
      formatter: (params) => {
        const p = (params as CallbackDataParams[]).find((item) => item.seriesName === '销量')
        if (!p) return ''
        return `${p.name}<br/>销量：${valueFmt(p.value as number)}`
      }
    },
    series: [
      {
        name: '轨道',
        type: 'bar',
        data: trackValues,
        barWidth,
        silent: true,
        barGap: '-100%',
        itemStyle: {
          color: trackColor,
          borderRadius: 999
        },
        z: 1
      },
      {
        name: '销量',
        type: 'bar',
        data: list.map((item, index) => ({
          value: item.value,
          itemStyle: {
            color: buildGradientColor(item.color, fallbackPalette[index % fallbackPalette.length]),
            borderRadius: 999
          }
        })),
        barWidth,
        z: 3,
        label: showValueLabel
          ? {
              show: true,
              position: 'right',
              distance: 14,
              color: '#ffe36b',
              fontSize: 15,
              fontWeight: 700,
              formatter: ({ value }) => valueFmt(value as number)
            }
          : undefined
      },
      {
        name: '端点',
        type: 'pictorialBar',
        symbol: 'circle',
        symbolPosition: 'end',
        symbolSize: 12,
        symbolOffset: [0, 0],
        silent: true,
        z: 4,
        data: list.map((item) => item.value),
        itemStyle: {
          color: dotColor,
          shadowBlur: 12,
          shadowColor: 'rgba(255,255,255,0.8)'
        }
      }
    ]
  }
}

/** ===================== BI大屏专用：透视伪3D实体饼图（贴近BI设计稿） ===================== */

export interface ThreeDPieItem {
  name: string
  value: number
  color?: string
}

export interface ThreeDPieOptionConfig {
  title?: string
  unit?: string
  centerTitle?: string
  centerValueFormatter?: FormatterFn
  labelFormatter?: (item: ThreeDPieItem, percent: number) => string
  showLegend?: boolean
  showCenterText?: boolean
  /** 支持 "58%" 或 ["34%", "63%"]，内部只取外半径 */
  radius?: string | [string, string]
  center?: [string, string]
  /** 实体厚度，px */
  depthOffset?: number
  /** 俯视压缩比，越小越扁 */
  tilt?: number
  /** 切片轻微外扩 */
  sliceGap?: number
  startAngle?: number
  backgroundColor?: string
  colorPalette?: string[]
  /** 外弧侧面加深 */
  sideDarkenRate?: number
  /** 左右切面加深 */
  radialDarkenRate?: number
  /** 标签线长度 */
  labelLength?: number
  labelLength2?: number
}

const TAU = Math.PI * 2

function normalizeOuterRadius(radius?: string | [string, string]) {
  if (!radius) return '58%'
  return Array.isArray(radius) ? radius[1] : radius
}

function percentToRadiusPx(percent: string, width: number, height: number) {
  return (Math.min(width, height) * parseFloat(percent)) / 200
}

function percentToCenterX(percent: string, width: number) {
  return (width * parseFloat(percent)) / 100
}

function percentToCenterY(percent: string, height: number) {
  return (height * parseFloat(percent)) / 100
}

function pointOnEllipse(cx: number, cy: number, rx: number, ry: number, rad: number) {
  return {
    x: cx + rx * Math.cos(rad),
    y: cy + ry * Math.sin(rad)
  }
}

function buildArcPoints(cx: number, cy: number, rx: number, ry: number, startRad: number, endRad: number, steps = 40) {
  const points: Array<[number, number]> = []
  const delta = endRad - startRad
  const count = Math.max(12, Math.ceil((Math.abs(delta) / (Math.PI / 12)) * (steps / 12)))

  for (let i = 0; i <= count; i++) {
    const t = startRad + (delta * i) / count
    const p = pointOnEllipse(cx, cy, rx, ry, t)
    points.push([p.x, p.y])
  }
  return points
}

function buildTopSectorPoints(cx: number, cy: number, rx: number, ry: number, startRad: number, endRad: number) {
  const arc = buildArcPoints(cx, cy, rx, ry, startRad, endRad, 48)
  return [[cx, cy], ...arc, [cx, cy]]
}

function buildOuterSidePoints(
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  depth: number,
  startRad: number,
  endRad: number
) {
  const topArc = buildArcPoints(cx, cy, rx, ry, startRad, endRad, 42)
  const bottomArc = buildArcPoints(cx, cy + depth, rx, ry, startRad, endRad, 42).reverse()
  return [...topArc, ...bottomArc]
}

function buildRadialSidePoints(cx: number, cy: number, rx: number, ry: number, depth: number, rad: number) {
  const topOuter = pointOnEllipse(cx, cy, rx, ry, rad)
  const bottomOuter = pointOnEllipse(cx, cy + depth, rx, ry, rad)

  return [
    [cx, cy],
    [topOuter.x, topOuter.y],
    [bottomOuter.x, bottomOuter.y],
    [cx, cy + depth]
  ]
}

function getFrontVisibleSegments(startRad: number, endRad: number) {
  const result: Array<[number, number]> = []
  let s = startRad
  let e = endRad

  if (e < s) e += TAU

  const minK = Math.floor(s / TAU) - 1
  const maxK = Math.ceil(e / TAU) + 1

  for (let k = minK; k <= maxK; k++) {
    const frontStart = k * TAU
    const frontEnd = k * TAU + Math.PI // sin(rad) > 0 的前半圈

    const segStart = Math.max(s, frontStart)
    const segEnd = Math.min(e, frontEnd)

    if (segEnd - segStart > 0.0001) {
      result.push([segStart, segEnd])
    }
  }

  return result
}

function isFrontAngle(rad: number) {
  return Math.sin(rad) > 0
}

export function threeDPieDonutOption(items: ThreeDPieItem[], cfg?: ThreeDPieOptionConfig): EChartsOption {
  const {
    title = '',
    unit = '',
    centerTitle = '',
    showLegend = false,
    showCenterText = false,
    center = ['50%', '61%'],
    depthOffset = 28,
    tilt = 0.46,
    sliceGap = 7,
    startAngle = 148,
    backgroundColor = 'transparent',
    sideDarkenRate = 0.34,
    radialDarkenRate = 0.2,
    labelLength = 18,
    labelLength2 = 12
  } = cfg ?? {}

  const outerRadius = normalizeOuterRadius(cfg?.radius)
  const colors = cfg?.colorPalette ?? (darkTheme.color as string[]) ?? ensureColors(cfg?.colorPalette)
  const valueFmt = cfg?.centerValueFormatter ?? defaultValueFormatter(unit)

  const pieData = items.map((item, index) => ({
    name: item.name,
    value: item.value,
    color: item.color ?? colors[index % colors.length]
  }))

  const total = pieData.reduce((sum, item) => sum + (Number(item.value) || 0), 0)

  let acc = 0
  const renderData = pieData.map((item) => {
    const percent = total ? item.value / total : 0
    const startRatio = acc
    const endRatio = acc + percent
    acc = endRatio
    return {
      ...item,
      percent,
      startRatio,
      endRatio,
      midRatio: (startRatio + endRatio) / 2
    }
  })

  /** 后面的先画，前面的后画 */
  const sortedForRender = [...renderData].sort((a, b) => {
    const aMid = (startAngle * Math.PI) / 180 + a.midRatio * TAU
    const bMid = (startAngle * Math.PI) / 180 + b.midRatio * TAU
    return Math.sin(aMid) - Math.sin(bMid)
  })

  return {
    backgroundColor,
    animation: false,
    title: title
      ? {
          text: title,
          left: 'left',
          top: 8,
          textStyle: {
            color: darkTheme.title?.textStyle?.color ?? '#eef8ff',
            fontSize: 16,
            fontWeight: 600
          }
        }
      : undefined,
    legend: showLegend
      ? {
          bottom: 8,
          left: 'center',
          itemWidth: 10,
          itemHeight: 10,
          textStyle: {
            color: darkTheme.textStyle?.color ?? '#c9e8ff',
            fontSize: 12
          },
          data: pieData.map((item) => item.name)
        }
      : undefined,
    tooltip: {
      trigger: 'item',
      backgroundColor: 'rgba(5, 30, 88, 0.94)',
      borderColor: 'rgba(86, 215, 255, 0.35)',
      textStyle: { color: '#ffffff' },
      formatter: (params) => {
        const current = pieData.find((item) => item.name === params.name)
        const percent = total ? Number(((Number(params.value) / total) * 100).toFixed(2)) : 0

        if (current && cfg?.labelFormatter) {
          return cfg.labelFormatter(current, percent)
        }
        return `${params.name}<br/>${valueFmt(params.value as number)} (${percent}%)`
      }
    },
    graphic: showCenterText
      ? [
          {
            type: 'text',
            left: 'center',
            top: '47%',
            style: {
              text: `${centerTitle ? `${centerTitle}\n` : ''}${valueFmt(total)}`,
              textAlign: 'center',
              fill: darkTheme.textStyle?.color ?? '#e6f7ff',
              fontSize: 18,
              fontWeight: 700,
              lineHeight: 26
            },
            z: 100
          }
        ]
      : undefined,
    series: [
      /** 阴影 */
      {
        name: 'pie-shadow',
        type: 'custom',
        silent: true,
        coordinateSystem: 'none',
        data: [1],
        z: 0,
        renderItem: (_params, api) => {
          const width = api.getWidth()
          const height = api.getHeight()

          const cx = percentToCenterX(center[0], width)
          const cy = percentToCenterY(center[1], height)
          const rx = percentToRadiusPx(outerRadius, width, height) * 0.98
          const ry = rx * tilt * 0.78

          return {
            type: 'ellipse',
            shape: {
              cx,
              cy: cy + depthOffset + 12,
              rx,
              ry: Math.max(ry * 0.5, 10)
            },
            style: {
              fill: 'rgba(0, 0, 0, 0.24)',
              shadowBlur: 30,
              shadowColor: 'rgba(0,0,0,0.28)'
            }
          }
        }
      },

      /** 3D 主体 */
      {
        name: 'pie-3d-body',
        type: 'custom',
        coordinateSystem: 'none',
        data: sortedForRender.map((item) => ({
          name: item.name,
          value: item.value
        })),
        z: 10,
        renderItem: (params, api) => {
          const width = api.getWidth()
          const height = api.getHeight()
          const current = sortedForRender[params.dataIndex]

          const baseCx = percentToCenterX(center[0], width)
          const baseCy = percentToCenterY(center[1], height)
          const rx = percentToRadiusPx(outerRadius, width, height)
          const ry = rx * tilt

          const startRad = (startAngle * Math.PI) / 180 + current.startRatio * TAU
          const endRad = (startAngle * Math.PI) / 180 + current.endRatio * TAU
          const midRad = (startRad + endRad) / 2

          const offsetX = Math.cos(midRad) * sliceGap
          const offsetY = Math.sin(midRad) * sliceGap * tilt

          const cx = baseCx + offsetX
          const cy = baseCy + offsetY

          const children: any[] = []

          /** 外弧前侧面 */
          const frontSegments = getFrontVisibleSegments(startRad, endRad)
          frontSegments.forEach(([segStart, segEnd]) => {
            children.push({
              type: 'polygon',
              shape: {
                points: buildOuterSidePoints(cx, cy, rx, ry, depthOffset, segStart, segEnd)
              },
              style: {
                fill: darkenHex(current.color, sideDarkenRate),
                stroke: 'rgba(0, 24, 82, 0.92)',
                lineWidth: 1.5
              }
            })
          })

          /** 起始切面 */
          if (isFrontAngle(startRad)) {
            children.push({
              type: 'polygon',
              shape: {
                points: buildRadialSidePoints(cx, cy, rx, ry, depthOffset, startRad)
              },
              style: {
                fill: darkenHex(current.color, radialDarkenRate),
                stroke: 'rgba(0, 24, 82, 0.88)',
                lineWidth: 1.2
              }
            })
          }

          /** 结束切面 */
          if (isFrontAngle(endRad)) {
            children.push({
              type: 'polygon',
              shape: {
                points: buildRadialSidePoints(cx, cy, rx, ry, depthOffset, endRad)
              },
              style: {
                fill: darkenHex(current.color, radialDarkenRate + 0.05),
                stroke: 'rgba(0, 24, 82, 0.88)',
                lineWidth: 1.2
              }
            })
          }

          /** 顶面 */
          children.push({
            type: 'polygon',
            shape: {
              points: buildTopSectorPoints(cx, cy, rx, ry, startRad, endRad)
            },
            style: {
              fill: current.color,
              stroke: 'rgba(0, 24, 82, 0.96)',
              lineWidth: 2.2,
              shadowBlur: 8,
              shadowColor: 'rgba(0, 208, 255, 0.12)'
            }
          })

          /** 顶部高光 */
          children.push({
            type: 'polygon',
            shape: {
              points: buildTopSectorPoints(cx, cy - 2, rx * 0.98, ry * 0.72, startRad, endRad)
            },
            style: {
              fill: 'rgba(255,255,255,0.08)',
              stroke: 'rgba(255,255,255,0)'
            },
            silent: true
          })

          return {
            type: 'group',
            children
          }
        }
      },

      /** 标签层 */
      {
        name: 'pie-label-layer',
        type: 'pie',
        radius: ['0%', outerRadius],
        center,
        startAngle,
        silent: true,
        z: 50,
        itemStyle: {
          color: 'rgba(0,0,0,0)',
          borderColor: 'rgba(0,0,0,0)',
          borderWidth: 0
        },
        label: {
          show: true,
          color: darkTheme.textStyle?.color ?? '#d7efff',
          fontSize: 12,
          fontWeight: 600,
          formatter: (params) => {
            const current = pieData.find((item) => item.name === params.name)
            const percent = total ? Number(((Number(params.value) / total) * 100).toFixed(0)) : 0

            if (current && cfg?.labelFormatter) {
              return cfg.labelFormatter(current, percent)
            }
            return `${params.name}\n${percent}%`
          }
        },
        labelLine: {
          show: true,
          length: labelLength,
          length2: labelLength2,
          lineStyle: {
            color: 'rgba(126, 210, 255, 0.78)',
            width: 1.5
          }
        },
        data: pieData.map((item) => ({
          name: item.name,
          value: item.value
        }))
      }
    ]
  }
}

function average(arr: number[]) {
  return arr.reduce((s, v) => s + v, 0) / arr.length
}

export interface MerchantSaleGrossRateData {
  x: string[]
  saleData: number[]
  grossRateData: number[]
}

export interface MerchantSaleGrossRateOptionExtra {
  title?: string
  saleName?: string
  grossRateName?: string
  saleUnit?: string
  rateUnit?: string
}

/**
 * 柱状图 + 折线图
 */
// 辅助函数：根据毛利率数据计算 Y 轴范围，确保图表有足够的空间展示正负毛利率
function getRateAxisRange(values: number[]) {
  const validValues = values.filter((item) => Number.isFinite(item))

  if (!validValues.length) {
    return {
      min: 0,
      max: 100
    }
  }

  const rawMin = Math.min(...validValues)
  const rawMax = Math.max(...validValues)

  // 至少包含 0，方便正负毛利率对比
  const minValue = Math.min(rawMin, 0)
  const maxValue = Math.max(rawMax, 0)

  const range = maxValue - minValue || 10
  const padding = Math.max(range * 0.15, 5)

  const min = minValue < 0 ? Math.floor((minValue - padding) / 10) * 10 : 0
  const max = maxValue > 0 ? Math.ceil((maxValue + padding) / 10) * 10 : 10

  return {
    min,
    max
  }
}

export function merchantSaleGrossRateOption(
  data: MerchantSaleGrossRateData,
  extra: MerchantSaleGrossRateOptionExtra = {}
): EChartsOption {
  const { title = '', saleName = '销售额', grossRateName = '毛利率', saleUnit = '', rateUnit = '%' } = extra

  const saleMax = Math.max(...data.saleData, 0)

  const safeSaleMax = saleMax > 0 ? undefined : 100

  const grossRateData = data.grossRateData.map((item) => Number(item) || 0)
  const rateAxisRange = getRateAxisRange(grossRateData)

  return {
    backgroundColor: 'transparent',

    // 关键：清掉旧 option 里可能残留的 graphic
    graphic: [],

    title: {
      text: title,
      left: 0,
      top: 0,
      textStyle: {
        color: '#333333',
        fontSize: 14,
        fontWeight: 500
      }
    },

    tooltip: {
      trigger: 'axis',
      backgroundColor: 'rgba(255,255,255,0.96)',
      borderColor: '#E5EAF3',
      borderWidth: 1,
      textStyle: {
        color: '#333333',
        fontSize: 12
      },
      axisPointer: {
        type: 'cross',
        crossStyle: {
          color: '#BFDFFF'
        }
      }
    },

    legend: {
      top: 0,
      left: 'center',
      itemGap: 28,
      itemWidth: 28,
      itemHeight: 10,
      textStyle: {
        color: '#333333',
        fontSize: 12
      },
      data: [saleName, grossRateName]
    },

    grid: {
      left: 36,
      right: 42,
      top: 58,
      bottom: 32,
      containLabel: true
    },

    xAxis: {
      type: 'category',
      data: data.x,
      name: '',
      axisTick: {
        show: true,
        alignWithLabel: true,
        lineStyle: {
          color: '#66BFFF'
        }
      },
      axisLine: {
        lineStyle: {
          color: '#66BFFF'
        }
      },
      axisLabel: {
        color: '#333333',
        fontSize: 12
      }
    },

    yAxis: [
      {
        type: 'value',
        min: 0,
        max: safeSaleMax,
        splitNumber: 5,
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#333333',
          fontSize: 12,
          formatter: (value) => {
            const num = Number(value) || 0
            if (num >= 100000000) return `${(num / 100000000).toFixed(1)}亿`
            if (num >= 10000) return `${(num / 10000).toFixed(1)}万`
            return `${num}${saleUnit}`
          }
        },
        splitLine: {
          lineStyle: {
            color: '#E1E7EF',
            type: 'dashed'
          }
        }
      },
      {
        type: 'value',
        min: rateAxisRange.min,
        max: rateAxisRange.max,
        position: 'right',
        name: rateUnit,
        nameLocation: 'end',
        nameTextStyle: {
          color: '#333333',
          fontSize: 14,
          padding: [0, 0, 0, 8]
        },
        axisLine: {
          show: false
        },
        axisTick: {
          show: false
        },
        axisLabel: {
          color: '#333333',
          fontSize: 12,
          formatter: '{value}'
        },
        splitLine: {
          show: false
        }
      }
    ],

    series: [
      {
        name: saleName,
        type: 'bar',
        yAxisIndex: 0,
        data: data.saleData,
        barWidth: 20,
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: false
          }
        },
        itemStyle: {
          borderRadius: [10, 10, 10, 10],
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: '#67C23A' },
              { offset: 0.5, color: '#BFD760' },
              { offset: 1, color: '#D66A3C' }
            ]
          }
        }
      },
      {
        name: grossRateName,
        type: 'line',
        yAxisIndex: 1,
        data: grossRateData,
        smooth: false,
        symbol: 'circle',
        symbolSize: 6,
        label: {
          show: false
        },
        emphasis: {
          label: {
            show: false
          }
        },
        lineStyle: {
          width: 2,
          color: '#2CA5FF'
        },
        itemStyle: {
          color: '#FFFFFF',
          borderColor: '#2CA5FF',
          borderWidth: 2
        },
        areaStyle: {
          color: {
            type: 'linear',
            x: 0,
            y: 0,
            x2: 0,
            y2: 1,
            colorStops: [
              { offset: 0, color: 'rgba(44, 165, 255, 0.45)' },
              { offset: 1, color: 'rgba(44, 165, 255, 0.03)' }
            ]
          }
        }
      }
    ]
  }
}
